import React, { useEffect, useRef, useState } from 'react';
import { Button, theme, Typography } from 'antd';

const { Text } = Typography;

// Safe expression evaluator — no eval()
function evaluateExpression(raw: string): string {
  const expr = raw.replace(/\s/g, '');
  if (!expr) return '';
  try {
    const result = parseAddSub(expr, { pos: 0 });
    if (!isFinite(result.value)) return 'Error';
    const rounded = Math.round(result.value * 1e10) / 1e10;
    return String(rounded);
  } catch {
    return 'Error';
  }
}

type Cursor = { pos: number };

function parseAddSub(expr: string, cur: Cursor): { value: number } {
  let left = parseMulDiv(expr, cur).value;
  while (
    cur.pos < expr.length &&
    (expr[cur.pos] === '+' || expr[cur.pos] === '-')
  ) {
    const op = expr[cur.pos++];
    const right = parseMulDiv(expr, cur).value;
    left = op === '+' ? left + right : left - right;
  }
  return { value: left };
}

function parseMulDiv(expr: string, cur: Cursor): { value: number } {
  let left = parseUnary(expr, cur).value;
  while (
    cur.pos < expr.length &&
    (expr[cur.pos] === '*' || expr[cur.pos] === '/')
  ) {
    const op = expr[cur.pos++];
    const right = parseUnary(expr, cur).value;
    if (op === '/' && right === 0) throw new Error('Division by zero');
    left = op === '*' ? left * right : left / right;
  }
  return { value: left };
}

function parseUnary(expr: string, cur: Cursor): { value: number } {
  if (expr[cur.pos] === '-') {
    cur.pos++;
    return { value: -parsePrimary(expr, cur).value };
  }
  if (expr[cur.pos] === '+') {
    cur.pos++;
  }
  return parsePrimary(expr, cur);
}

function parsePrimary(expr: string, cur: Cursor): { value: number } {
  if (expr[cur.pos] === '(') {
    cur.pos++;
    const val = parseAddSub(expr, cur).value;
    if (expr[cur.pos] !== ')') throw new Error('Missing )');
    cur.pos++;
    return { value: val };
  }
  return parseNumber(expr, cur);
}

function parseNumber(expr: string, cur: Cursor): { value: number } {
  const start = cur.pos;
  while (
    cur.pos < expr.length &&
    ((expr[cur.pos] >= '0' && expr[cur.pos] <= '9') || expr[cur.pos] === '.')
  ) {
    cur.pos++;
  }
  if (cur.pos === start)
    throw new Error(`Unexpected: ${expr[cur.pos] ?? 'end'}`);
  return { value: parseFloat(expr.slice(start, cur.pos)) };
}

type ButtonDef = {
  label: string;
  value?: string;
  action?: 'clear' | 'backspace' | 'equals';
  variant?: 'operator' | 'action' | 'equals';
};

const BUTTONS: ButtonDef[][] = [
  [
    { label: 'C', action: 'clear', variant: 'action' },
    { label: '⌫', action: 'backspace', variant: 'action' },
    { label: '(', value: '(' },
    { label: ')', value: ')' },
  ],
  [
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
    { label: '÷', value: '/', variant: 'operator' },
  ],
  [
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '×', value: '*', variant: 'operator' },
  ],
  [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '−', value: '-', variant: 'operator' },
  ],
  [
    { label: '0', value: '0' },
    { label: '.', value: '.' },
    { label: '=', action: 'equals', variant: 'equals' },
    { label: '+', value: '+', variant: 'operator' },
  ],
];

const CalculatorPage: React.FC = () => {
  const { token } = theme.useToken();
  const [expression, setExpression] = useState('');
  const [evalResult, setEvalResult] = useState('');
  const [justEvaluated, setJustEvaluated] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const handleButton = (btn: ButtonDef) => {
    if (btn.action === 'clear') {
      setExpression('');
      setEvalResult('');
      setJustEvaluated(false);
      return;
    }
    if (btn.action === 'backspace') {
      if (justEvaluated) {
        setExpression('');
        setEvalResult('');
        setJustEvaluated(false);
      } else {
        const next = expression.slice(0, -1);
        setExpression(next);
        setEvalResult(next ? evaluateExpression(next) : '');
      }
      return;
    }
    if (btn.action === 'equals') {
      const result = evaluateExpression(expression);
      setEvalResult(result);
      if (result !== 'Error') {
        setExpression(result);
        setJustEvaluated(true);
      }
      return;
    }
    const next =
      justEvaluated && btn.value && /[\d(]/.test(btn.value)
        ? btn.value
        : expression + (btn.value ?? '');
    setExpression(next);
    setEvalResult(evaluateExpression(next));
    setJustEvaluated(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const key = e.key;
    if (/^\d$/.test(key)) {
      handleButton({ label: key, value: key });
      return;
    }
    if (key === '+') {
      handleButton({ label: '+', value: '+', variant: 'operator' });
      return;
    }
    if (key === '-') {
      handleButton({ label: '−', value: '-', variant: 'operator' });
      return;
    }
    if (key === '*') {
      handleButton({ label: '×', value: '*', variant: 'operator' });
      return;
    }
    if (key === '/') {
      e.preventDefault();
      handleButton({ label: '÷', value: '/', variant: 'operator' });
      return;
    }
    if (key === '.') {
      handleButton({ label: '.', value: '.' });
      return;
    }
    if (key === '(') {
      handleButton({ label: '(', value: '(' });
      return;
    }
    if (key === ')') {
      handleButton({ label: ')', value: ')' });
      return;
    }
    if (key === 'Enter' || key === '=') {
      handleButton({ label: '=', action: 'equals', variant: 'equals' });
      return;
    }
    if (key === 'Backspace') {
      handleButton({ label: '⌫', action: 'backspace', variant: 'action' });
      return;
    }
    if (key === 'Escape' || key === 'c' || key === 'C') {
      handleButton({ label: 'C', action: 'clear', variant: 'action' });
      return;
    }
  };

  const displayValue = justEvaluated ? evalResult : expression || '0';
  const previewValue =
    !justEvaluated &&
    evalResult &&
    evalResult !== 'Error' &&
    evalResult !== expression
      ? `= ${evalResult}`
      : '';

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 16,
        outline: 'none',
        height: '100vh',
        background: token.colorBgContainer,
        boxSizing: 'border-box',
      }}
    >
      <Text strong style={{ fontSize: 15 }}>
        Calculator
      </Text>

      {/* Display */}
      <div
        style={{
          background: token.colorFillAlter,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          padding: '12px 16px',
          minHeight: 80,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          gap: 4,
        }}
      >
        <Text
          type="secondary"
          style={{
            fontSize: 13,
            textAlign: 'right',
            minHeight: 18,
            wordBreak: 'break-all',
            display: 'block',
            fontFamily: 'monospace',
          }}
        >
          {previewValue}
        </Text>
        <Text
          style={{
            fontSize: 26,
            fontFamily: 'monospace',
            textAlign: 'right',
            wordBreak: 'break-all',
            lineHeight: 1.2,
            display: 'block',
            color: token.colorText,
          }}
        >
          {displayValue}
        </Text>
      </div>

      {/* Button grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 8,
          flex: 1,
        }}
      >
        {BUTTONS.flat().map((btn, i) => {
          const isOperator = btn.variant === 'operator';
          const isAction = btn.variant === 'action';
          const isEquals = btn.variant === 'equals';

          return (
            <Button
              key={i}
              size="large"
              onClick={() => handleButton(btn)}
              type={isEquals ? 'primary' : 'default'}
              danger={isAction}
              style={{
                height: '100%',
                minHeight: 52,
                fontSize: 18,
                fontWeight: isOperator || isEquals ? 500 : 400,
                borderRadius: token.borderRadius,
                color:
                  isEquals || isAction
                    ? undefined
                    : isOperator
                      ? token.colorPrimary
                      : token.colorText,
                background:
                  isEquals || isAction
                    ? undefined
                    : isOperator
                      ? token.colorPrimaryBg
                      : token.colorFillSecondary,
                borderColor:
                  isEquals || isAction
                    ? undefined
                    : isOperator
                      ? token.colorPrimaryBorder
                      : 'transparent',
              }}
            >
              {btn.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default CalculatorPage;
